import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SubPermissionCardProps {
  name: string;
  description: string;
  isEnabled: boolean;
  isEditMode: boolean;
  onToggle: () => void;
}

export function SubPermissionCard({
  name,
  description,
  isEnabled,
  isEditMode,
  onToggle,
}: Readonly<SubPermissionCardProps>) {
  return (
    <Card 
      className={cn(
        'transition-all duration-200 h-full',
        isEditMode && 'hover:shadow-md cursor-pointer',
        isEnabled && 'ring-2 ring-green-500/20 bg-green-50/30 dark:bg-green-950/10'
      )}
      onClick={isEditMode ? onToggle : undefined}
    >
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 h-full">
          {/* Header with Status Badge */}
          <div className="flex items-start justify-between gap-2">
            <div className={cn(
              'rounded-lg p-2 flex items-center justify-center shrink-0',
              isEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'
            )}>
              {isEnabled ? (
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <X className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            
            {/* Checkbox */}
            <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isEnabled}
                onCheckedChange={onToggle}
                disabled={!isEditMode}
              />
            </div>
          </div>

          {/* Title and Description */}
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-sm leading-tight">{name}</h4>
            </div>
            
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {description}
            </p>
          </div>

          {/* Status Badge at Bottom */}
          <div className="pt-2 border-t">
            <Badge 
              variant={isEnabled ? 'success' : 'secondary'}
              accent={isEnabled ? 'success' : undefined}
              className="text-xs w-full justify-center"
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
